import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import logo from '../assets/logo.png';

const creds = [
  ['Coaching', 'CSC-Qualified Chess Coach', 'The professional standard for chess instruction in the UK, awarded by Chess in Schools & Communities — the national charity for chess education. Robert is an accredited CSC coach actively delivering the schools programme.'],
  ['Safeguarding', 'Enhanced DBS & Safeguarding', 'Enhanced DBS Disclosure checked. Full Child and Vulnerable Adult Safeguarding certification. Fully compliant for work with children, young people, and vulnerable adults in all settings — schools, cruise ships, care homes, and community venues.'],
  ['Insurance', '£5 Million Public Liability', '£5 million public liability insurance covering all teaching and coaching activities. Meets the compliance requirements of cruise lines, NHS-linked organisations, schools, and all other operating environments.'],
];

const projects = [
  ['Cruise Enrichment', 'Chess at Sea', 'A professional chess enrichment residency for cruise passengers. Active discussions with RWS Global, Fred. Olsen, Saga Cruises, P&O Cruises, and Cunard Line. Conference call completed with RWS Global enrichment booking team.', 'Active — discussions ongoing'],
  ['Community Health', 'Chess for Wellbeing', 'A social prescribing programme for NHS commissioners and Primary Care Networks. Proposals submitted to Public Health Wales and Powys Teaching Health Board. Targeting the National Academy for Social Prescribing community fund.', 'Proposals submitted'],
  ['Events', 'The Great Merthyr Schools Chess Tournament 2026', 'Headline sponsor of TGMSCT 2026 — Friday 3rd July, Dowlais Community Centre. Special guest Michael Adams. Organised by the StephensandGeorge Charitable Trust.', 'Confirmed — 3rd July 2026'],
  ['Culture', 'Abergavenny Chess Centre', 'A proposal to establish a permanent chess centre in Abergavenny — making it the chess capital of Wales. Backed by Jonathan Blackburn, Development Director of the Welsh Chess Union. Proposal submitted to Alun Griffiths CBE.', 'Proposal in progress'],
];

const audience = [
  'Cruise lines and enrichment management companies',
  'NHS commissioners and social prescribing teams',
  'Schools, Multi-Academy Trusts, and education authorities',
  'Community organisations and cultural venues',
  'Event organisers and tournament directors',
  'Media and press enquiries',
  'Potential sponsors and partners',
];

export default function About() {
  return (
    <>
      <PageHero
        tag="About"
        title={<>Robert <em>Palser</em></>}
        lede="Chess Tutor. Founder of Chess at Sea. Based in the Brecon Beacons, Wales."
      />

      <section className="profile">
        <div className="inner">
          <div className="profile-grid">
            <Reveal className="profile-card">
              <img src={logo} alt="Chess at Sea emblem" className="pc-emblem" />
              <div className="profile-name">Robert Palser</div>
              <div className="profile-title">Chess Tutor</div>
              <div className="profile-location">Bwlch, Powys, Wales</div>
              <div className="profile-contact">
                <a href="tel:07710666500">07710 666500</a>
                <a href="mailto:robertpalser@outlook.com">robertpalser@outlook.com</a>
                <a href="http://www.chesstuition.org" target="_blank" rel="noreferrer">www.chesstuition.org</a>
              </div>
            </Reveal>
            <Reveal className="profile-right" delay={120}>
              <h2>A life in <em>chess</em></h2>
              <p>
                Robert Palser has spent many years making chess accessible — and meaningful — to people
                of all ages and backgrounds. From the chess clubs he has founded, to the schools and
                private pupils he has coached, to the cruise enrichment programme he is now bringing to
                the world&rsquo;s great ocean liners, chess has been both a professional calling and a
                genuine passion.
              </p>
              <p>
                Robert is a CSC-qualified chess coach — holding the professional standard qualification
                for chess instruction in the UK — with extensive experience teaching children, adults,
                older adults, and players at every level of ability. He holds full Enhanced DBS
                Disclosure, Child and Vulnerable Adult Safeguarding certification, and £5 million public
                liability insurance.
              </p>
              <p>
                He lives in Bwlch, Powys — in the heart of the Brecon Beacons, between Abergavenny and
                Brecon — and the Welsh chess community is central to his work. As well as Chess at Sea,
                he is developing proposals to bring chess to the NHS social prescribing agenda, to
                schools across Wales and England, and to a permanent chess centre in Abergavenny that
                would make the town the chess capital of Wales.
              </p>
              <p>
                The centrepiece of his teaching technology is the SenseRobot — the world&rsquo;s first AI
                chess robot with a physical robotic arm that moves real pieces on a real board — which he
                owns and deploys across all his programmes.
              </p>
              <div className="pullquote">
                <p>“Chess delivers something that almost no other enrichment activity can: a skill passengers — or students, or patients — take home with them. That is what drives everything I do.”</p>
                <cite>— Robert Palser</cite>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="creds-section">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Credentials &amp; Compliance</p>
            <h2 className="section-title">Qualified, <em>certified, insured</em></h2>
            <div className="divider" />
          </Reveal>
          <div className="creds-grid">
            {creds.map(([tag, h, p], i) => (
              <Reveal key={h} className="cred-card" delay={i * 90}>
                <p className="cred-tag">{tag}</p>
                <h3>{h}</h3>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="projects on-dark">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Current Projects</p>
            <h2 className="section-title">Everything <em>in motion</em></h2>
            <div className="divider" />
          </Reveal>
          <div className="proj-grid">
            {projects.map(([tag, h, p, status], i) => (
              <Reveal key={h} className="proj-card" delay={(i % 2) * 110}>
                <p className="proj-tag">{tag}</p>
                <h3>{h}</h3>
                <p>{p}</p>
                <span className="proj-status">{status}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Get in Touch</p>
            <h2 className="section-title">Let&rsquo;s talk <em>chess</em></h2>
            <div className="divider" />
          </Reveal>
          <div className="contact-grid">
            <Reveal className="contact-details">
              <h3>Contact Robert Palser</h3>
              <div className="contact-item-large">
                <span className="ci-label">Telephone</span>
                <span className="ci-value"><a href="tel:07710666500">07710 666500</a></span>
              </div>
              <div className="contact-item-large">
                <span className="ci-label">Email</span>
                <span className="ci-value"><a href="mailto:robertpalser@outlook.com">robertpalser@outlook.com</a></span>
              </div>
              <div className="contact-item-large">
                <span className="ci-label">Website</span>
                <span className="ci-value"><a href="http://www.chesstuition.org" target="_blank" rel="noreferrer">www.chesstuition.org</a></span>
              </div>
              <div className="contact-item-large">
                <span className="ci-label">Location</span>
                <span className="ci-value" style={{ fontSize: 18 }}>Bwlch, Powys, Wales</span>
              </div>
            </Reveal>
            <Reveal className="contact-form-box" delay={120}>
              <h3>Who should get in touch?</h3>
              <p>Robert welcomes enquiries from anyone interested in any aspect of the Chess at Sea programme or his wider chess activities.</p>
              <ul className="audience-list">
                {audience.map((a) => <li key={a}>{a}</li>)}
              </ul>
              <a href="mailto:robertpalser@outlook.com" className="btn-primary">Send an Enquiry <span className="arrow">→</span></a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
